import { useEffect, useState } from 'react';
import {
  NavigateOptions,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { cn, Dialog, DialogContent } from '@qodo/design-system';
import { NAVIGATION_MODE } from './const';
import { MultiStepDialogContext } from './multiStepDialogContext';
import { Footer } from './parts/Footer';
import { Header } from './parts/Header';
import { Sidebar } from './parts/Sidebar';
import { MultiStepDialogProps, StepConfig } from './types';
import { canNavigateToStep } from './utils';

export const MultiStepDialog = ({
  title,
  steps = [],
  currentStepIndex: controlledStepIndex,
  navigationMode = NAVIGATION_MODE.FORWARD_ONLY,
  onStepChange = () => {},
  onCancel: propsOnCancel = () => {},
  onComplete = () => {},
  onStepViewed,
  onStepNavigated,
  className: propsClassName,
  contentClassName,
  outletClassName,
  disableOutsideClick = false,
  disableEscapeKey = false,
  defaultFurthestStepIndex = 0,
  defaultEarliestStepIndexAvailable = 0,
}: MultiStepDialogProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [completedSteps, setCompletedSteps] = useState(new Set<number>());
  const [disabledSteps, setDisabledSteps] = useState(new Set<string>());
  const [hiddenSteps, setHiddenSteps] = useState(new Set<string>());
  const [furthestStepReached, setFurthestStepReached] = useState(
    defaultFurthestStepIndex,
  );
  const [earliestStepAvailable, setEarliestStepAvailable] = useState(
    defaultEarliestStepIndexAvailable,
  );
  const [isPending, setIsPending] = useState(false);
  const [isNextEnabled, setIsNextEnabled] = useState(true);

  const stepPaths = steps.map((step) => step.path);
  const locationBasedIndex = stepPaths.indexOf(location.pathname);

  const isControlled = controlledStepIndex !== undefined;
  const currentStepIndex = isControlled
    ? controlledStepIndex
    : locationBasedIndex;

  const currentStep =
    currentStepIndex >= 0 ? steps[currentStepIndex] : ({} as StepConfig);
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === steps.length - 1;

  useEffect(() => {
    if (defaultFurthestStepIndex > 0) {
      setCurrentStepAsFurthestStepReached();
    }
  }, []);

  useEffect(() => {
    if (currentStepIndex < 0) {
      const wizardBasePath = stepPaths[0]?.split('/').slice(0, -1).join('/');
      const isStillInWizard =
        wizardBasePath && location.pathname.startsWith(wizardBasePath);

      if (isStillInWizard) {
        navigateToStep(0, { replace: true });
      }

      return;
    }

    const wouldBeRedirected =
      currentStepIndex > 0 &&
      !canNavigateToStep(
        currentStepIndex,
        steps,
        navigationMode,
        furthestStepReached,
        earliestStepAvailable,
      );

    if (wouldBeRedirected) return;

    const step = steps[currentStepIndex];
    const isFirstVisit = currentStepIndex > furthestStepReached;
    const stepControlsNextEnabled = step?.isNextInitiallyEnabled === false;

    if (isFirstVisit) {
      // First visit: apply the step's initial value
      setIsNextEnabled(step?.isNextInitiallyEnabled ?? true);
    } else if (!stepControlsNextEnabled) {
      // Return visit: reset to true unless the step wants to control isNextEnabled
      setIsNextEnabled(true);
    }

    setIsPending(false);
    onStepViewed?.(currentStepIndex, step?.id ?? '');
  }, [currentStepIndex]);

  useEffect(() => {
    if (currentStepIndex <= 0) return;

    const isCurrentStepAccessible =
      canNavigateToStep(
        currentStepIndex,
        steps,
        navigationMode,
        furthestStepReached,
        earliestStepAvailable,
      ) && currentStepIndex > 0;

    if (!isCurrentStepAccessible) {
      navigateToStep(earliestStepAvailable);
    }
  }, [currentStepIndex, navigationMode, furthestStepReached, location.search]);

  const isCookieDialogVisible = () => {
    const cookieDialog = document.getElementById('cookiescript_injected');
    const cookieWrapper = document.getElementById(
      'cookiescript_injected_wrapper',
    );

    if (cookieDialog || cookieWrapper) {
      const isVisible =
        cookieDialog?.offsetParent !== null ||
        cookieWrapper?.offsetParent !== null;

      return isVisible;
    }

    return false;
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      if (isCookieDialogVisible()) {
        return;
      }

      propsOnCancel(currentStepIndex, currentStep.id);
    }
  };

  const onCancel = () => {
    propsOnCancel(currentStepIndex, currentStep.id);
  };

  const onPointerDownOutside = (event: Event) => {
    if (disableOutsideClick) {
      event.preventDefault();

      return;
    }

    if (isCookieDialogVisible()) {
      event.preventDefault();
    }
  };

  const onEscapeKeyDown = (event: KeyboardEvent) => {
    if (disableEscapeKey) {
      event.preventDefault();
    }
  };

  const setStepDisabled = (stepId: string, disabled: boolean) => {
    setDisabledSteps((prev) => {
      const next = new Set(prev);

      if (disabled) next.add(stepId);
      else next.delete(stepId);

      return next;
    });
  };

  const setStepHidden = (stepId: string, hidden: boolean) => {
    setHiddenSteps((prev) => {
      const next = new Set(prev);

      if (hidden) next.add(stepId);
      else next.delete(stepId);

      return next;
    });
  };

  const markStepComplete = (index: number) => {
    setCompletedSteps((prev) => new Set(prev).add(index));
  };

  const navigateToStep = (index: number, options?: NavigateOptions) => {
    const step = steps[index];

    if (!step) {
      console.warn(`Cannot navigate to step ${index}: step does not exist`);

      return;
    }

    if (isControlled) {
      onStepChange(index, step.id);
    } else {
      navigate({ pathname: step.path, search: location.search }, options);
    }
  };

  const goToIndex = (index: number) => {
    if (
      !canNavigateToStep(
        index,
        steps,
        navigationMode,
        furthestStepReached,
        earliestStepAvailable,
      )
    )
      return;

    navigateToStep(index);
  };

  const goNext = () => {
    markStepComplete(currentStepIndex);
    onStepNavigated?.(currentStepIndex, currentStep.id, 'next');

    if (!isLast) {
      const nextStepIndex = currentStepIndex + 1;

      setFurthestStepReached((prev) => Math.max(prev, nextStepIndex));
      navigateToStep(nextStepIndex);
    } else {
      onComplete();
    }
  };

  const goBack = () => {
    if (currentStepIndex <= 0) return;

    onStepNavigated?.(currentStepIndex, currentStep.id, 'back');
    navigateToStep(currentStepIndex - 1);
  };

  const setCurrentStepAsFurthestStepReached = () => {
    setFurthestStepReached(currentStepIndex);
    const completedSteps = new Set<number>();

    for (let i = 0; i < currentStepIndex; i++) {
      completedSteps.add(i);
    }

    setCompletedSteps(completedSteps);
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'p-0 gap-0 text-body leading-5 flex flex-col h-198 max-h-[90vh] overflow-hidden',
          propsClassName,
        )}
        showCloseButton={false}
        onPointerDownOutside={onPointerDownOutside}
        onEscapeKeyDown={onEscapeKeyDown}
      >
        <MultiStepDialogContext.Provider
          value={{
            completedSteps,
            currentStep,
            currentStepIndex,
            furthestStepReached,
            setCurrentStepAsFurthestStepReached,
            setFurthestStepReached,
            earliestStepAvailable,
            setEarliestStepAvailable,
            onCancel,
            onComplete,
            goNext,
            goBack,
            goToIndex,
            isFirst,
            isLast,
            isNextEnabled,
            isPending,
            navigationMode,
            setIsNextEnabled,
            setIsPending,
            steps,
            disabledSteps,
            setStepDisabled,
            hiddenSteps,
            setStepHidden,
          }}
        >
          <div className="shrink-0">
            <Header title={title} />
          </div>

          <div className={cn('flex flex-1 min-h-0', contentClassName)}>
            <Sidebar />
            <div
              className={cn(
                'flex-1 min-h-0 overflow-auto p-10',
                outletClassName,
              )}
            >
              <Outlet />
            </div>
          </div>

          <div className="shrink-0">
            <Footer />
          </div>
        </MultiStepDialogContext.Provider>
      </DialogContent>
    </Dialog>
  );
};
